import { useEffect, useId, useRef, useState, type KeyboardEvent, type MouseEvent, type PointerEvent as ReactPointerEvent, type ReactNode, type Ref } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";

export interface RailMenuEntry {
  id: string;
  label: string;
  // Etiqueta corta para donde no cabe `label` (barra segmentada en móvil).
  short?: string;
  hint: string;
  icon: string;
  route: string;
  // Rutas en las que esta entrada es "la actual".
  match: RegExp;
  badge?: string;
}

export interface RailMenuTriggerProps {
  ref: Ref<HTMLButtonElement>;
  "aria-haspopup": "menu";
  "aria-expanded": boolean;
  "aria-controls": string;
  onClick: (e: MouseEvent<HTMLButtonElement>) => void;
  onKeyDown: (e: KeyboardEvent<HTMLButtonElement>) => void;
  onPointerEnter: (e: ReactPointerEvent) => void;
  onPointerLeave: () => void;
}

interface Props {
  label: string;
  entries: RailMenuEntry[];
  isDarkMode: boolean;
  // Navegar dentro de la misma app (sin cortina) o pasar por el cambio de app.
  sameApp: boolean;
  onLeaveApp: (route: string) => void;
  children: (trigger: RailMenuTriggerProps) => ReactNode;
}

// Menú flotante de un acceso del riel, como el de Informes en Mapon: el ícono
// deja de ir directo a una ruta y ofrece las variantes de la app. Se abre con
// clic/tap, al pasar el mouse (con retardo, para que cruzar el riel no lo
// dispare) y con teclado (Enter, Espacio o flechas); Esc lo cierra y devuelve
// el foco al ícono.
export default function RailMenu({ label, entries, isDarkMode, sameApp, onLeaveApp, children }: Props) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const id = useId();
  const [open, setOpen] = useState(false);
  const [top, setTop] = useState(0);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const focusOnOpen = useRef<"first" | "last" | null>(null);
  // Abierto por hover: se cierra al salir. Abierto por clic/teclado: se queda.
  const byHover = useRef(false);

  const show = (focus: "first" | "last" | null = null, hover = false) => {
    clearTimeout(timer.current);
    byHover.current = hover;
    const r = triggerRef.current?.getBoundingClientRect();
    if (r) setTop(Math.max(8, Math.min(r.top - 6, window.innerHeight - 260)));
    focusOnOpen.current = focus;
    setOpen(true);
  };
  const hide = (restoreFocus = false) => {
    clearTimeout(timer.current);
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  };
  const items = () => Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);

  useEffect(() => {
    if (!open) return;
    if (focusOnOpen.current) {
      const list = items();
      (focusOnOpen.current === "first" ? list[0] : list[list.length - 1])?.focus();
    }
    const outside = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!menuRef.current?.contains(t) && !triggerRef.current?.contains(t)) hide();
    };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [open]);

  useEffect(() => () => clearTimeout(timer.current), []);
  // Cambió la ruta (navegó desde el menú o desde otro lado): el menú sobra.
  useEffect(() => setOpen(false), [pathname]);

  const pick = (e: RailMenuEntry) => {
    hide();
    if (sameApp) navigate(e.route);
    else onLeaveApp(e.route);
  };

  const onMenuKey = (e: KeyboardEvent) => {
    const list = items();
    const i = list.indexOf(document.activeElement as HTMLElement);
    const go = (n: number) => {
      e.preventDefault();
      list[(n + list.length) % list.length]?.focus();
    };
    if (e.key === "ArrowDown") go(i + 1);
    else if (e.key === "ArrowUp") go(i - 1);
    else if (e.key === "Home") go(0);
    else if (e.key === "End") go(list.length - 1);
    else if (e.key === "Escape") {
      e.preventDefault();
      hide(true);
    } else if (e.key === "Tab") hide();
  };

  const trigger: RailMenuTriggerProps = {
    ref: triggerRef,
    "aria-haspopup": "menu",
    "aria-expanded": open,
    "aria-controls": id,
    // detail === 0: clic generado por Enter/Espacio → el foco entra al menú.
    // Si ya estaba abierto por hover, el clic lo fija en vez de cerrarlo.
    onClick: (e) => {
      if (open && byHover.current) byHover.current = false;
      else if (open) hide();
      else show(e.detail === 0 ? "first" : null);
    },
    onKeyDown: (e) => {
      if (e.key === "ArrowDown" || e.key === "ArrowRight") {
        e.preventDefault();
        show("first");
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        show("last");
      } else if (e.key === "Escape" && open) hide();
    },
    // Sólo mouse: en táctil el pointerenter llega junto con el tap y abriría y
    // cerraría en el mismo gesto.
    onPointerEnter: (e) => {
      if (e.pointerType !== "mouse") return;
      clearTimeout(timer.current);
      if (!open) timer.current = setTimeout(() => show(null, true), 140);
    },
    onPointerLeave: () => {
      clearTimeout(timer.current);
      if (byHover.current || !open) timer.current = setTimeout(() => setOpen(false), 220);
    },
  };

  const surface = isDarkMode ? "bg-dark-card border-dark-border shadow-black/40" : "bg-white border-gray-200 shadow-gray-900/10";
  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";

  return (
    <>
      {children(trigger)}
      {/* Portal a body: dentro del riel (fixed z-30) el z del menú sólo competía
          dentro de esa capa y en Administración de Flotilla el panel lateral
          (z-40) lo tapaba; sólo asomaba la orilla entre el panel y el contenido. */}
      {open && createPortal(
        <div
          ref={menuRef}
          id={id}
          role="menu"
          aria-label={label}
          onKeyDown={onMenuKey}
          onPointerEnter={() => clearTimeout(timer.current)}
          onPointerLeave={trigger.onPointerLeave}
          style={{ top }}
          // Nace desde el ícono (origen a la izquierda), 150 ms ease-out.
          className={`fixed left-[3.75rem] z-[55] w-80 rounded-2xl border p-1.5 shadow-xl origin-left motion-safe:animate-scaleIn ${surface}`}
        >
          <p className={`px-3 pt-1.5 pb-1 text-[11px] font-semibold uppercase tracking-wider ${muted}`}>{label}</p>
          {entries.map((e) => {
            const current = e.match.test(pathname);
            return (
              <button
                key={e.id}
                type="button"
                role="menuitem"
                aria-current={current ? "page" : undefined}
                onClick={() => pick(e)}
                className={`group w-full flex items-start gap-3 px-3 py-2.5 rounded-xl text-left outline-none transition-colors duration-150 ease-out focus-visible:ring-2 focus-visible:ring-primary-500 ${
                  isDarkMode ? "hover:bg-white/5 focus-visible:bg-white/5" : "hover:bg-gray-100 focus-visible:bg-gray-100"
                }`}
              >
                <span
                  className={`w-9 h-9 flex-shrink-0 rounded-lg flex items-center justify-center text-lg transition-colors duration-150 ${
                    current ? "bg-primary-500 text-white" : "bg-primary-500/15 text-primary-500"
                  }`}
                >
                  <i className={e.icon} aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <span className={`flex items-center gap-2 text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                    {e.label}
                    {e.badge && (
                      <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-600">{e.badge}</span>
                    )}
                  </span>
                  <span className={`block text-xs mt-0.5 ${muted}`}>{e.hint}</span>
                </span>
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </>
  );
}
